import { addMonths, startOfDay, subBusinessDays } from 'date-fns'
import { getCountFromServer, Timestamp } from 'firebase/firestore'
import type { Cursor } from '~/composables/useFirestore'
import type { PlannedDeliveryProcessingStartedEvent } from '~/models/DomainEvents/PlannedDeliveryProcessingStartedEvent'
import {
  getPuppetProcessingDate,
  getPuppetProcessingStatus,
} from '~/utils/dateHelpers/getPuppetProcessingDate'

const PLANNED_DELIVERY_TABS: PlannedDeliveryTab[] = [
  'toCheck',
  'checkAgain',
  'checked',
  'readyToPrescribe',
  'prescribed',
  'shipped',
  'failed',
]

const today = startOfDay(new Date())

async function mapListDocument(data: PlannedDelivery) {
  // TODO: Optimize this query as multiple deliveries can have the same user, currently this results in N+1 queries
  const user = await appUsersService.getUser(data.userId)

  const deliveryDate = data.expectedDeliveryDate.toDate()
  const processingDate = getPuppetProcessingDate(deliveryDate)
  const processingDateStartOfDay = processingDate
    ? startOfDay(processingDate)
    : undefined
  const processingStatus = getPuppetProcessingStatus(
    data.status,
    today,
    processingDateStartOfDay,
  )

  return {
    ...data,
    autoClickerProcessingDetails: {
      date: processingDate,
      status: processingStatus,
    },
    user,
  }
}

async function mapDetailDocument(data: PlannedDelivery) {
  const deliveryItems = await DeliveryItemsService.getDeliveryItems(data.id)

  return {
    ...data,
    deliveryItems,
  } as PlannedDeliveryDetail
}

interface QueryOptions {
  limit?: number
  cursor?: Cursor
}

function getStatusQuery(
  filters: PlannedDeliveryFilters,
  options?: QueryOptions,
) {
  const { limit, cursor } = options ?? {}
  const { query } = useFirestore()

  const start = Timestamp.fromDate(new Date(filters?.start ?? 0))
  const end = Timestamp.fromDate(
    new Date(filters?.end ?? addMonths(new Date(), 3)),
  )

  const q = query<PlannedDelivery>(
    'PlannedDeliveries',
    (q) => {
      q.status.is(filters.status)
      q.expectedDeliveryDate.isLessThanOrEqual(end)
      q.expectedDeliveryDate.isGreaterThanOrEqual(start)
    },
    {
      limit: limit ?? 9999,
      sort: {
        name: 'ExpectedDeliveryDate',
        direction: 'asc',
      },
      cursor,
    },
  )

  return q
}

export const PlannedDeliveriesService = {
  async getCounts(filters: PlannedDeliveryFilters) {
    const result: PlannedDeliveryCount = {}
    for await (const tab of PLANNED_DELIVERY_TABS) {
      const countQuery = getStatusQuery({
        ...filters,
        status: TAB_TO_STATUS[tab],
      })
      const count = await getCountFromServer(countQuery)
      result[tab] = count.data().count
    }

    return result
  },

  async getPlannedDeliveries<T = PlannedDelivery>(options: {
    target: Ref<T[]>
    filters: PlannedDeliveryFilters
    limit?: number
    abortSignal?: AbortSignal
    mapper?: (data: PlannedDelivery) => Promise<T> | T
    cursor?: Cursor
  }) {
    const { target, filters, abortSignal, mapper, cursor, limit } = options

    const query = getStatusQuery(filters, { limit: limit ?? 20, cursor })
    const { getDocuments } = useFirestore()

    abortSignal?.throwIfAborted()

    await getDocuments(query, {
      target,
      signal: abortSignal,
      map:
        mapper
        ?? (mapListDocument as unknown as (
          data: PlannedDelivery,
        ) => Promise<T> | T),
    })
  },

  async getPlannedDeliveriesByUserId(
    userId: string,
    target: Ref<PlannedDeliveryDetail[]>,
    signal?: AbortSignal,
  ) {
    const { getDocuments, query } = useFirestore()

    const start = Timestamp.fromDate(subBusinessDays(new Date(), 5))

    const q = query<PlannedDelivery>(
      'PlannedDeliveries',
      (q) => {
        q.userId.is(userId)
        q.expectedDeliveryDate.isGreaterThanOrEqual(start)
      },
      {
        sort: {
          name: 'ExpectedDeliveryDate',
          direction: 'asc',
        },
      },
    )

    return await getDocuments(q, {
      map: mapDetailDocument,
      target,
      signal,
    })
  },

  async getPlannedDeliveryDetail(
    plannedDeliveryId: string,
    target: Ref<PlannedDeliveryDetail>,
    signal?: AbortSignal,
  ) {
    const { getDocument } = useFirestore()

    return await getDocument(['PlannedDeliveries', plannedDeliveryId], {
      map: mapDetailDocument,
      target,
      signal,
    })
  },

  async getPlannedDelivery(
    plannedDeliveryId: string,
    target?: Ref<PlannedDelivery>,
    signal?: AbortSignal,
  ) {
    const { getDocument } = useFirestore()

    return await getDocument(['PlannedDeliveries', plannedDeliveryId], {
      map: mapListDocument,
      signal,
      target,
    })
  },

  async retryAutoClicker(deliveryIds: string[]) {
    if (deliveryIds.length === 0) throw new Error('No delivery ids provided')

    const distinctDeliveryIds = Array.from(new Set(deliveryIds))

    await sendDomainEvent(
      'autoclicker',
      'retry-failed-planned-deliveries',
      {
        deliveries: distinctDeliveryIds.map(id => ({ deliveryId: id })),
      },
      crypto.randomUUID(),
    )
  },

  async forceChangeDeliveryItemStatus(
    deliveryId: string,
    deliveryItemId: string,
    autoClickerStatus: AutoClickerStatus,
  ) {
    await sendDomainEvent(
      'planned-deliveries',
      'force-change-delivery-item-status',
      {
        deliveryId,
        deliveryItemId,
        // The backend's force-change-delivery-item-status schema names this
        // field `status` (it holds an AutoClickerStatus value).
        status: autoClickerStatus,
      },
      deliveryItemId,
    )
  },

  async sendPlannedDeliveryProcessingStartedEvent(
    event: PlannedDeliveryProcessingStartedEvent,
  ): Promise<void> {
    const id = crypto.randomUUID()
    await sendDomainEvent(
      'planned-deliveries',
      'processing-started',
      { ...event },
      id,
    )
  },

  async sendForceDeliveryPrescribedEvent(deliveryId: string) {
    await sendDomainEvent(
      'planned-deliveries',
      'force-prescribed',
      {
        deliveryId,
      },
      deliveryId,
    )
  },

  async updateExpectedDeliveryDate(
    deliveryId: string,
    expectedDeliveryDate: Date,
  ) {
    await sendDomainEvent(
      'planned-deliveries',
      'expected-delivery-date-updated',
      {
        deliveryId,
        expectedDeliveryDate,
      },
      deliveryId,
    )
  },

  async linkTicket(
    deliveryId: string,
    ticketUrl: string,
  ) {
    await sendDomainEvent(
      'planned-deliveries',
      'ticket-linked',
      {
        deliveryId,
        ticketUrl,
      },
      deliveryId,
    )
  },

  async unlinkTicket(
    deliveryId: string,
  ) {
    await sendDomainEvent(
      'planned-deliveries',
      'ticket-unlinked',
      {
        deliveryId,
      },
      deliveryId,
    )
  },

  async refreshAnomalies() {
    const config = useRuntimeConfig()
    const { cloudFunctionRequestApiPost } = useApi()
    await cloudFunctionRequestApiPost(
      'Features-requestVerifyPlannedDeliveries',
      {},
      {
        headers: {
          'Access-Control-Allow-Origin': '*',
          'x-api-key': `${config.public.CF_API_STOCK_API_KEY}`,
        },
      },
    )
  },
}
